import Vue from 'vue';
import Vuex, { StoreOptions } from 'vuex';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { StripeActions } from './modules/stripe/actions';
import { FormsActions } from './modules/forms/actions';
import { DbGetters } from './modules/db/getters';
import { DbActions } from './modules/db/actions';
import { AuthGetters } from './modules/auth/getters';
import { AuthActions } from './modules/auth/actions';
import { UserGetters } from './modules/user/getters';
import { UserActions } from './modules/user/actions';
import { ProgramGetters } from './modules/programs/getters';
import { ProgramActions } from './modules/programs/actions';
import { FileStorageActions } from './modules/fileStorage/actions';
import { ToolActions } from './modules/tools/actions';
import { ToolGetters } from './modules/tools/getters';
import auth from './modules/auth';
import user from './modules/user';
import programs from './modules/programs';
import question from './modules/questions';
import realmApp from './modules/realmApp';
import { RootState } from './state';
import tools from './modules/tools';
import fileStorage from './modules/fileStorage';
import db from './modules/db';
import toolState from './modules/tools/state';
import formState from './modules/forms/state';
import realmAppState from './modules/realmApp/state';
import fileStorageState from './modules/fileStorage/state';
import authState from './modules/auth/state';
import userState from './modules/user/state';
import programState from './modules/programs/state';
import questionState from './modules/questions/state';
import { QuestionGetters } from './modules/questions/getters';
import { actions as QuestionActions } from './modules/questions/actions';
import dbState from './modules/db/state';
import { mutations as dbMutations } from './modules/db/mutations';
import stripe from './modules/stripe';
import formsApp from './modules/forms';
import stripeState from './modules/stripe/state';

Vue.use(Vuex);

const store: StoreOptions<RootState> = {
  modules: {
    auth,
    user,
    question,
    realmApp,
    tools,
    fileStorage,
    db,
    stripe,
    programs,
    formsApp
  }
};

const vuexStore = new Vuex.Store<RootState>(store);

export const {
  useState: useFormsState,
  useMutations: useFormsMutations,
  useActions: useFormsActions
} = createNamespacedHelpers<typeof formState, any, FormsActions>(vuexStore, 'formsApp');
export const {
  useState: useToolState,
  useGetters: useToolGetters,
  useMutations: useToolMutations,
  useActions: useToolActions
} = createNamespacedHelpers<typeof toolState, ToolGetters, ToolActions, any>(vuexStore, 'tools');
export const {
  useState: useRealmAppState,
  useGetters: useRealmAppGetters,
  useMutations: useRealmAppMutations,
  useActions: useRealmAppActions
} = createNamespacedHelpers<typeof realmAppState, any, any, any>(vuexStore, 'realmApp');
export const {
  useState: useFileStorageState,
  useGetters: useFileStorageGetters,
  useMutations: useFileStorageMutations,
  useActions: useFileStorageActions
} = createNamespacedHelpers<typeof fileStorageState, any, FileStorageActions, any>(
  vuexStore,
  'fileStorage'
);
export const {
  useState: useAuthState,
  useGetters: useAuthGetters,
  useMutations: useAuthMutations,
  useActions: useAuthActions
} = createNamespacedHelpers<typeof authState, AuthGetters, AuthActions, any>(vuexStore, 'auth');
export const {
  useState: useQuestionState,
  useGetters: useQuestionGetters,
  useMutations: useQuestionMutations,
  useActions: useQuestionActions
} = createNamespacedHelpers<typeof questionState, QuestionGetters, typeof QuestionActions, any>(
  vuexStore,
  'question'
);
export const {
  useState: useUserState,
  useGetters: useUserGetters,
  useMutations: useUserMutations,
  useActions: useUserActions
} = createNamespacedHelpers<typeof userState, UserGetters, UserActions, any>(vuexStore, 'user');

export const {
  useState: useProgramState,
  useGetters: useProgramGetters,
  useMutations: useProgramMutations,
  useActions: useProgramActions
} = createNamespacedHelpers<typeof programState, ProgramGetters, ProgramActions, any>(
  vuexStore,
  'programs'
);

export const {
  useState: useDbState,
  useGetters: useDbGetters,
  useMutations: useDbMutations,
  useActions: useDbActions
} = createNamespacedHelpers<typeof dbState, DbGetters, DbActions, typeof dbMutations>(
  vuexStore,
  'db'
);
export const {
  useState: useStripeState,
  useGetters: useStripeGetters,
  useMutations: useStripeMutations,
  useActions: useStripeActions
} = createNamespacedHelpers<typeof stripeState, any, StripeActions, any>(vuexStore, 'stripe');

export default vuexStore;
